import React, { useState, useEffect } from "react";
import Modal from "components/modal";
import { MdArrowBack, MdArrowForward, MdCheck } from "react-icons/md";

const InscriptionWithMembreFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialInscription,
  anneesUniv = [],
  niveaux = [],
  parcours = [],
  mentions = [],
  promotions = [],
  loading = false
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [inscription, setInscription] = useState({
    createMembreDto: {
      nom_prenom_membre: "",
      com_origin: "",
      adresse_membre: "",
      tel_membre: "",
      num_matricule: "",
      cin_membre: "",
      id_promotion: ""
    },
    id_anneuniv: "",
    id_niveau: "",
    id_parcours: "",
    id_mention: "",
    date_inscrit: new Date().toISOString().split('T')[0]
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      if (initialInscription) {
        setInscription({
          createMembreDto: {
            nom_prenom_membre: initialInscription.createMembreDto?.nom_prenom_membre || "",
            com_origin: initialInscription.createMembreDto?.com_origin || "",
            adresse_membre: initialInscription.createMembreDto?.adresse_membre || "",
            tel_membre: initialInscription.createMembreDto?.tel_membre || "",
            num_matricule: initialInscription.createMembreDto?.num_matricule || "",
            cin_membre: initialInscription.createMembreDto?.cin_membre || "",
            id_promotion: initialInscription.createMembreDto?.id_promotion || ""
          },
          id_anneuniv: initialInscription.id_anneuniv || "",
          id_niveau: initialInscription.id_niveau || "",
          id_parcours: initialInscription.id_parcours || "",
          id_mention: initialInscription.id_mention || "",
          date_inscrit: initialInscription.date_inscrit || new Date().toISOString().split('T')[0]
        });
        setValidationErrors({});
      }
    }
  }, [isOpen, initialInscription]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('createMembreDto.')) {
      const membreField = name.split('.')[1];
      setInscription({
        ...inscription,
        createMembreDto: {
          ...inscription.createMembreDto,
          [membreField]: value
        }
      });
      
      if (validationErrors[`createMembreDto.${membreField}`]) {
        setValidationErrors({
          ...validationErrors,
          [`createMembreDto.${membreField}`]: null,
        });
      }
    } else {
      setInscription({ ...inscription, [name]: value });

      if (validationErrors[name]) {
        setValidationErrors({
          ...validationErrors,
          [name]: null,
        });
      }
    }
  };

  const validateStep1 = () => {
    const errors = {};

    if (!inscription.createMembreDto.nom_prenom_membre.trim()) {
      errors["createMembreDto.nom_prenom_membre"] = "Le nom et prénom sont requis";
    }

    if (!inscription.createMembreDto.com_origin.trim()) {
      errors["createMembreDto.com_origin"] = "La commune d'origine est requise";
    }

    if (!inscription.createMembreDto.tel_membre.trim()) {
      errors["createMembreDto.tel_membre"] = "Le numéro de téléphone est requis";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = () => {
    const errors = {};

    if (!inscription.id_anneuniv) {
      errors.id_anneuniv = "L'année universitaire est requise";
    }

    if (!inscription.id_niveau) {
      errors.id_niveau = "Le niveau est requis";
    }

    if (!inscription.id_parcours) {
      errors.id_parcours = "Le parcours est requis";
    }

    if (!inscription.id_mention) {
      errors.id_mention = "La mention est requise";
    }

    if (!inscription.date_inscrit) {
      errors.date_inscrit = "La date d'inscription est requise";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const moveToNextStep = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    }
  };

  const moveToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (currentStep === 1) {
      if (validateStep1()) {
        moveToNextStep();
      }
      return;
    }
    
    if (!validateStep2()) {
      return;
    }

    setSubmitting(true);
    
    const result = await onSubmit(inscription);
    
    if (!result.success) {
      setValidationErrors(result.errors || {});
    }
    
    setSubmitting(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="3xl">
      <div className="text-center sm:text-left">
        <div className="mb-4">
          <h3 className="text-xl font-semibold text-gray-800">
            Nouvelle inscription avec création d'étudiant
          </h3>
          <div className="h-1 w-12 bg-indigo-600 mt-2 rounded-full"></div>
        </div>

        {/* Stepper */}
        <div className="mb-6">
          <div className="flex items-center justify-center">
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${currentStep === 1 ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-indigo-300 text-indigo-600'}`}>
                1
              </div>
              <span className={`ml-2 text-sm ${currentStep === 1 ? 'font-medium text-indigo-600' : 'text-gray-500'}`}>Informations étudiant</span>
            </div>
            <div className={`flex-1 h-0.5 mx-4 ${currentStep === 2 ? 'bg-indigo-600' : 'bg-gray-300'}`}></div>
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${currentStep === 2 ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-indigo-300 text-gray-500'}`}>
                2
              </div>
              <span className={`ml-2 text-sm ${currentStep === 2 ? 'font-medium text-indigo-600' : 'text-gray-500'}`}>Détails inscription</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Step 1: Informations étudiant */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-700 mb-4">Informations de l'étudiant</h4>
              
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="nom_prenom_membre"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Nom et prénom *
                  </label>
                  <input
                    type="text"
                    name="createMembreDto.nom_prenom_membre"
                    id="nom_prenom_membre"
                    value={inscription.createMembreDto.nom_prenom_membre}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    placeholder="Ex: Jean Dupont"
                  />
                  {validationErrors["createMembreDto.nom_prenom_membre"] && (
                    <p className="mt-1.5 text-sm text-red-600">
                      {validationErrors["createMembreDto.nom_prenom_membre"]}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="tel_membre"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Téléphone *
                  </label>
                  <input
                    type="text"
                    name="createMembreDto.tel_membre"
                    id="tel_membre"
                    value={inscription.createMembreDto.tel_membre}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    placeholder="Ex: +261 34 12 345 67"
                  />
                  {validationErrors["createMembreDto.tel_membre"] && (
                    <p className="mt-1.5 text-sm text-red-600">
                      {validationErrors["createMembreDto.tel_membre"]}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="com_origin"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Commune d'origine *
                  </label>
                  <input
                    type="text"
                    name="createMembreDto.com_origin"
                    id="com_origin"
                    value={inscription.createMembreDto.com_origin}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    placeholder="Ex: Antananarivo"
                  />
                  {validationErrors["createMembreDto.com_origin"] && (
                    <p className="mt-1.5 text-sm text-red-600">
                      {validationErrors["createMembreDto.com_origin"]}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="adresse_membre"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Adresse
                  </label>
                  <input
                    type="text"
                    name="createMembreDto.adresse_membre"
                    id="adresse_membre"
                    value={inscription.createMembreDto.adresse_membre}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    placeholder="Ex: Lot II F 45"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label
                    htmlFor="num_matricule"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Numéro matricule
                  </label>
                  <input
                    type="text"
                    name="createMembreDto.num_matricule"
                    id="num_matricule"
                    value={inscription.createMembreDto.num_matricule}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    placeholder="Ex: H8765"
                  />
                </div>

                <div>
                  <label
                    htmlFor="cin_membre"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Numéro CIN
                  </label>
                  <input
                    type="text"
                    name="createMembreDto.cin_membre"
                    id="cin_membre"
                    value={inscription.createMembreDto.cin_membre}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    placeholder="Ex: 205021000902000"
                  />
                </div>
                
                <div>
                  <label
                    htmlFor="id_promotion"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Promotion
                  </label>
                  <select
                    id="id_promotion"
                    name="createMembreDto.id_promotion"
                    value={inscription.createMembreDto.id_promotion}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                  >
                    <option value="">Sélectionner une promotion</option>
                    {promotions.map(promotion => (
                      <option key={promotion.id_promotion} value={promotion.id_promotion}>
                        {promotion.name_promotion}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Informations inscription */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <h4 className="text-lg font-medium text-gray-700 mb-4">Informations d'inscription</h4>
              
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="id_anneuniv"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Année universitaire *
                  </label>
                  <select
                    id="id_anneuniv"
                    name="id_anneuniv"
                    value={inscription.id_anneuniv}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                  >
                    <option value="">Sélectionner une année</option>
                    {anneesUniv.map(annee => (
                      <option key={annee.id_anneuniv} value={annee.id_anneuniv}>
                        {annee.dateuniv}
                      </option>
                    ))}
                  </select>
                  {validationErrors.id_anneuniv && (
                    <p className="mt-1.5 text-sm text-red-600">
                      {validationErrors.id_anneuniv}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="id_niveau"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Niveau *
                  </label>
                  <select
                    id="id_niveau"
                    name="id_niveau"
                    value={inscription.id_niveau}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                  >
                    <option value="">Sélectionner un niveau</option>
                    {niveaux.map(niveau => (
                      <option key={niveau.id_niveau} value={niveau.id_niveau}>
                        {niveau.name_niveau}
                      </option>
                    ))}
                  </select>
                  {validationErrors.id_niveau && (
                    <p className="mt-1.5 text-sm text-red-600">
                      {validationErrors.id_niveau}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="id_parcours"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Parcours *
                  </label>
                  <select
                    id="id_parcours"
                    name="id_parcours"
                    value={inscription.id_parcours}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                  >
                    <option value="">Sélectionner un parcours</option>
                    {parcours.map(p => (
                      <option key={p.id_parcours} value={p.id_parcours}>
                        {p.name_parcours}
                      </option>
                    ))}
                  </select>
                  {validationErrors.id_parcours && (
                    <p className="mt-1.5 text-sm text-red-600">
                      {validationErrors.id_parcours}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="id_mention"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Mention *
                  </label>
                  <select
                    id="id_mention"
                    name="id_mention"
                    value={inscription.id_mention}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                  >
                    <option value="">Sélectionner une mention</option>
                    {mentions.map(mention => (
                      <option key={mention.id_mention} value={mention.id_mention}>
                        {mention.name_mention}
                      </option>
                    ))}
                  </select>
                  {validationErrors.id_mention && (
                    <p className="mt-1.5 text-sm text-red-600">
                      {validationErrors.id_mention}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="date_inscrit"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Date d'inscription *
                </label>
                <input
                  type="date"
                  name="date_inscrit"
                  id="date_inscrit"
                  value={inscription.date_inscrit}
                  onChange={handleInputChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                />
                {validationErrors.date_inscrit && (
                  <p className="mt-1.5 text-sm text-red-600">
                    {validationErrors.date_inscrit}
                  </p>
                )}
              </div>

              {/* Résumé des informations de l'étudiant */}
              <div className="mt-6 bg-gray-50 p-4 rounded-md border border-gray-200">
                <h5 className="font-medium text-sm text-gray-700 mb-2">Informations de l'étudiant</h5>
                <div className="grid grid-cols-1 gap-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Nom et prénom:</span>
                    <span className="font-medium">{inscription.createMembreDto.nom_prenom_membre}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Téléphone:</span>
                    <span>{inscription.createMembreDto.tel_membre}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Commune d'origine:</span>
                    <span>{inscription.createMembreDto.com_origin}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {validationErrors.general && (
            <div className="rounded-md bg-red-50 p-3 border border-red-200">
              <p className="text-sm text-red-600">
                {validationErrors.general}
              </p>
            </div>
          )}

          <div className="mt-8 flex justify-between">
            {currentStep > 1 ? (
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 rounded-md text-indigo-600 bg-white border border-indigo-300 hover:bg-indigo-50 transition-colors"
                onClick={moveToPreviousStep}
                disabled={submitting || loading}
              >
                <MdArrowBack className="mr-2 h-5 w-5" />
                Retour
              </button>
            ) : (
              <div></div> // Espace vide pour maintenir l'alignement
            )}
            
            <div className="flex gap-3">
              <button
                type="button"
                className="px-4 py-2 rounded-md text-gray-600 border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
                onClick={onClose}
                disabled={submitting || loading}
              >
                Annuler
              </button>
              
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-sm"
                disabled={submitting || loading}
              >
                {(submitting || loading) ? (
                  <span className="flex items-center">
                    <svg
                      className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Chargement...
                  </span>
                ) : currentStep === 1 ? (
                  <>
                    <span>Suivant</span>
                    <MdArrowForward className="ml-2 h-5 w-5" />
                  </>
                ) : (
                  <>
                    <MdCheck className="mr-2 h-5 w-5" />
                    <span>Inscrire</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default InscriptionWithMembreFormModal;